import React, { useEffect, useCallback } from "react";
import { InternalContent } from "@components/library";
import TopBalance from "@components/library/TopBalance";
import "./index.sass";
import { useSelector } from "react-redux";
import InfoPopup from "@components/pages/Wallet/components/InfoPopup";
import { useTranslation } from "@helpers/translate";
import { Helmet } from "react-helmet";
import axios from "@helpers/axios/private.axios";
import { useNavigate } from "react-router-dom";
import { WALLET_TYPE } from "@constants";
import DemoWarning from "../components/DemoWarning";

import DepositStepper from "./components/DepositStepper";
import DepositMethodStep from "./components/DepositMethodStep";
import PaymentDetailsStep from "./components/PaymentDetailsStep";
import PaymentProcessStep from "./components/PaymentProcessStep";
import PaymentExecutionStep from "./components/PaymentExecutionStep";

const DEFAULT_DEPOSIT_AMOUNT = "100";

export const Deposit = () => {
  const {
    theme: { theme },
    wallet: { active_wallet, loading: isWalletLoading },
  } = useSelector((state) => state);
  // Поповнення йде лише на реальний рахунок, у демо-режимі показуємо підказку
  const isRealWallet = active_wallet?.type === WALLET_TYPE.REAL_FUTURES;

  const { __ } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = React.useState(1);
  const [selectedMethod, setSelectedMethod] = React.useState(null);
  const [amount, setAmount] = React.useState(DEFAULT_DEPOSIT_AMOUNT);

  const [depositLoading, setDepositLoading] = React.useState(false);
  const [depositError, setDepositError] = React.useState("");
  const [depositQuote, setDepositQuote] = React.useState(null);
  const [depositTransaction, setDepositTransaction] = React.useState(null);
  const [transactionDetails, setTransactionDetails] = React.useState(null);
  const [depositAddress, setDepositAddress] = React.useState(null);
  const [payportPayment, setPayportPayment] = React.useState(null);
  const [paymentExecutionData, setPaymentExecutionData] = React.useState(null);

  const handleBackStep = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const resetPaymentProcessData = () => {
    setDepositTransaction(null);
    setTransactionDetails(null);
    setDepositAddress(null);
    setPayportPayment(null);
    setPaymentExecutionData(null);
  };

  const handleAmountChange = (value) => {
    setAmount(value);
    setDepositQuote(null);
  };

  const handleSelectMethod = (method) => {
    setDepositError("");
    setDepositQuote(null);
    resetPaymentProcessData();
    setSelectedMethod(method);
    setAmount(DEFAULT_DEPOSIT_AMOUNT);
    setStep(2);
  };

  const getDepositQuote = async (payload) => {
    const response = await axios.post("/wallet/deposit-quote", payload);
    const quoteData = response?.data?.data || response?.data;

    setDepositQuote(quoteData);

    return quoteData;
  };

  const createCryptoDepositAddress = async () => {
    const response = await axios.get("/wallet/deposit-address", {
      params: {
        asset: selectedMethod?.asset,
        network: selectedMethod?.network,
      },
    });

    const depositData = response?.data?.data || response?.data;

    setDepositAddress(depositData);

    setDepositTransaction({
      transaction: {
        id: depositData?.transaction_id,
      },
    });
  };

  const createGatewayDeposit = async (details) => {
    const payload = {
      amount: details.amount,
    };

    const response = await axios.post(selectedMethod.endpoint, payload);
    const depositData = response?.data?.data || response?.data;

    setDepositTransaction(depositData);

    const transactionId = depositData?.transaction?.id;

    if (transactionId) {
      try {
        const transactionResponse = await axios.get(
          `/transaction/${transactionId}`,
        );

        setTransactionDetails(
          transactionResponse?.data?.data || transactionResponse?.data,
        );
      } catch (transactionError) {
        console.log(
          "Transaction details error:",
          transactionError?.response?.data || transactionError,
        );
      }
    }
  };

  const handleNextFromDetails = async (details) => {
    try {
      setDepositLoading(true);
      setDepositError("");
      resetPaymentProcessData();

      if (selectedMethod?.type === "crypto") {
        if (!depositQuote) {
          await getDepositQuote({
            asset: selectedMethod?.asset,
            network: selectedMethod?.network,
            usd_amount: details.amount,
          });
        }

        await createCryptoDepositAddress();
        setStep(3);
        return;
      }

      await createGatewayDeposit(details);
      setStep(3);
    } catch (err) {
      console.log("Deposit process error:", err?.response?.data || err);

      setDepositError(
        err?.response?.data?.message ||
          err?.message ||
          "Deposit creation failed",
      );
    } finally {
      setDepositLoading(false);
    }
  };

  const handleGoToWallet = () => {
    setStep(1);
    setAmount(DEFAULT_DEPOSIT_AMOUNT);
    setSelectedMethod(null);
    setDepositError("");
    setDepositQuote(null);

    resetPaymentProcessData();
  };

  const checkTransactionStatus = useCallback(
    async (transactionId) => {
      try {
        const response = await axios.get(`/transaction/${transactionId}`);
        const transaction = response?.data?.data || response?.data;

        setTransactionDetails(transaction);

        if (transaction?.status === 2) {
          setPaymentExecutionData({
            amount:
              transaction?.amount_credited ||
              transaction?.amount ||
              depositQuote?.usd_amount ||
              amount,
            asset: transaction?.currency || "USD",
            network:
              transaction?.network ||
              depositAddress?.network ||
              selectedMethod?.network,
            transactionId: transaction?.id,
            dateTime: transaction?.created_at,
            processingTime: selectedMethod?.processingTime || "Instantly",
          });

          setStep(4);
          return;
        }

        if (transaction?.status === 0) {
          setDepositError("Transaction declined");
        }
      } catch (error) {
        console.log(
          "Transaction status error:",
          error?.response?.data || error,
        );
      }
    },
    [
      amount,
      depositQuote,
      depositAddress?.network,
      selectedMethod?.asset,
      selectedMethod?.network,
      selectedMethod?.processingTime,
    ],
  );

  useEffect(() => {
    const transactionId =
      depositAddress?.transaction_id || depositTransaction?.transaction?.id;

    if (step !== 3 || !transactionId) return;

    checkTransactionStatus(transactionId);

    const interval = setInterval(() => {
      checkTransactionStatus(transactionId);
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [
    step,
    depositAddress?.transaction_id,
    depositTransaction?.transaction?.id,
    checkTransactionStatus,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, [step]);

  return (
    <InternalContent
      bgImg={`../images/bg/bg-lamba3${theme === "white" ? "-white" : ""}.png`}
    >
      <Helmet>
        <title>
          {__("seo.deposit_funds")} {__("seo.divider")} {__("seo.title")}
        </title>
      </Helmet>

      <TopBalance />

      <InfoPopup />

      {/* Поки гаманці не завантажились, активний рахунок невідомий: не блимаємо підказкою */}
      {isWalletLoading ? null : isRealWallet ? (
        <div className="deposit">
          <DepositStepper currentStep={step} />

          {step === 1 && (
            <DepositMethodStep
              selectedMethod={selectedMethod}
              onSelectMethod={handleSelectMethod}
            />
          )}

          {step === 2 && (
            <PaymentDetailsStep
              selectedMethod={selectedMethod}
              amount={amount}
              depositQuote={depositQuote}
              onAmountChange={handleAmountChange}
              onGetDepositQuote={getDepositQuote}
              onBack={handleBackStep}
              onNext={handleNextFromDetails}
              loading={depositLoading}
              depositError={depositError}
            />
          )}

          {step === 3 && (
            <PaymentProcessStep
              selectedMethod={selectedMethod}
              amount={amount}
              depositQuote={depositQuote}
              depositAddress={depositAddress}
              depositTransaction={depositTransaction}
              transactionDetails={transactionDetails}
              payportPayment={payportPayment}
              onBack={handleBackStep}
              loading={depositLoading}
            />
          )}

          {step === 4 && (
            <PaymentExecutionStep
              amount={paymentExecutionData?.amount || amount}
              network={paymentExecutionData?.network}
              transactionId={paymentExecutionData?.transactionId}
              dateTime={paymentExecutionData?.dateTime}
              processingTime={paymentExecutionData?.processingTime}
              onGoToWallet={handleGoToWallet}
              onGoToTrading={() => navigate("/")}
            />
          )}
        </div>
      ) : (
        <DemoWarning />
      )}
    </InternalContent>
  );
};

export default Deposit;
